/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, FormControl, Grid, Heading, HFlow, Paper, Text, useTheme, VFlow } from 'bold-ui'
import { DateField, RadioField, RichTextField } from 'components/form'
import { InfoIcon } from 'components/InfoIcon'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import moment from 'moment'
import { useMemo } from 'react'
import { MetaPath } from 'util/metaPath'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { MedicoesPanel, MedicoesPanelModel } from '../../../components/MedicoesPanel/MedicoesPanel'
import { NecessidadesEspeciaisField } from './components/NecessidadesEspeciaisField'

export interface ObjetivoFormModel {
  texto?: string
  dum?: string
  vacinacaoEmDia?: boolean
  medicoes?: MedicoesPanelModel
  necessidadesEspeciais?: boolean
}

export interface ObjetivoFormProps {
  name: MetaPath<ObjetivoFormModel>
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
  tipoAtendProf: TipoAtendimentoProfissional
}

export function ObjetivoForm(props: ObjetivoFormProps) {
  const { name, cidadao, dataAtendimento, tipoAtendProf } = props
  const theme = useTheme()
  const isAtendimentoOdonto = tipoAtendProf === TipoAtendimentoProfissional.ATENDIMENTO_ODONTOLOGICO

  const diffDumDays = useMemo(() => moment().diff(cidadao.ultimaDum?.dum, 'd'), [cidadao.ultimaDum])

  return (
    <Grid>
      <Cell size={12}>
        <RichTextField
          name={name.texto}
          maxLength={4000}
          placeholder='Insira as demais informações relacionadas ao exame físico.'
        />
      </Cell>

      {cidadao.informarDum && (
        <Cell size={8}>
          <HFlow>
            <DateField
              name={name.dum}
              label={
                <HFlow hSpacing={0.5} alignItems='center'>
                  DUM
                  <InfoIcon icon='infoCircleFilled' text='Preencher mesmo se a cidadã estiver com dúvida.' />
                </HFlow>
              }
              minDate={moment(cidadao.dataNascimento).toDate()}
              maxDate={moment(dataAtendimento).toDate()}
            />
            <Paper
              style={css`
                border: 0;
                background: ${theme.pallete.surface.background};
                padding: 0.5rem;
              `}
            >
              <VFlow vSpacing={0.25}>
                <HFlow hSpacing={0.5}>
                  <Heading level={4}>Última DUM:</Heading>
                  <Text component='span'>
                    {cidadao.ultimaDum?.dum &&
                      `${moment(cidadao.ultimaDum.dum).format('DD/MM/YYYY')} ${
                        diffDumDays > 0 ? `(${diffDumDays} dia${diffDumDays === 1 ? '' : 's'})` : ''
                      }`}
                  </Text>
                </HFlow>
                <Text component='span' fontStyle='italic'>
                  {cidadao.ultimaDum?.registradoEm
                    ? `Registrada em: ${moment(cidadao.ultimaDum.registradoEm).format('DD/MM/YYYY')}`
                    : 'Não registrada'}
                </Text>
              </VFlow>
            </Paper>
          </HFlow>
        </Cell>
      )}

      <Cell size={12}>
        <MedicoesPanel name={name.medicoes} cidadao={cidadao} />
      </Cell>

      <Cell size={12}>
        <FormControl label='Vacinação em dia?'>
          <HFlow hSpacing={1}>
            <RadioField name={name.vacinacaoEmDia} label='Sim' value='true' />
            <RadioField name={name.vacinacaoEmDia} label='Não' value='false' />
          </HFlow>
        </FormControl>
      </Cell>

      {isAtendimentoOdonto && (
        <Cell size={12}>
          <NecessidadesEspeciaisField name={name.necessidadesEspeciais} label='Paciente com necessidades especiais' />
        </Cell>
      )}
    </Grid>
  )
}
